import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DeviceShadowService {
  url = environment.deviceShadow;

  constructor(private httpClient: HttpClient) { }

  getDeviceShadows(request: ShadowQueryRequest): Promise<IShadowQueryResponse> {
    return this.httpClient
      .post<IShadowQueryResponse>(`${this.url}/deviceShadow`, request)
      .toPromise();
  }

  deleteScrollId(scrollId: string): Promise<void> {
    return this.httpClient
      .delete<void>(`${this.url}/deviceShadow?scrollId=${scrollId}`)
      .toPromise();
  }

  async requestCsv(query: ShadowQueryRequest): Promise<string> {
    const response = await this.httpClient
      .post<CsvResponse>(`${this.url}/deviceShadow/csvReports`, query)
      .toPromise();
    return response.requestId;
  }

  getCsvUrl(requestId: string): Promise<SignedUrlResponse> {
    return this.httpClient
      .get<SignedUrlResponse>(
        `${this.url}/deviceShadow/csvReports/${requestId}/signedUrl`,
      )
      .toPromise();
  }
}

export interface IShadowQueryResponse {
  results: IDeviceShadowRecord[];
  total: number;
  scrollId: string;
}

export interface IDeviceShadowBase {
  cableType?: string;
  can1Type?: string;
  companyId?: number;
  companyName?: string;
  diliRecordNumber?: string;
  firmwareVersionAuthoritativeEntity?: string;
  followerSerial?: string;
  followerState?: string;
  groupName?: string;
  hardwareVersion?: string;
  installerType?: string;
  intendedFirmwareVersion?: string;
  platform?: string;
  serialNumber?: string;
  statusId?: number;
  vehicleCalibration?: string;
  vehicleClass?: string;
  vehicleId?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleName?: string;
  vehicleYear?: string;
  vin?: string;
}

export interface ShadowQueryRequest extends IDeviceShadowBase {
  batteryVoltage?: IRangeNumber;
  cameraHeight?: IRangeNumber;
  cameraOffset?: IRangeNumber;
  checkIn?: IRangeDate;
  continuousDataEnabled?: string;
  count?: number;
  deviceDetectedBusType?: string;
  deviceType? : string;
  ecmEngineHours?: IRangeDate;
  ecmEngineSpeed?: IRangeDate;
  ecmOdometer?: IRangeDate;
  ecmSpeed?: IRangeDate;
  ecmVin?: IRangeDate;
  firmwareVersion?: string;
  geotabSerialNumber?: string;
  hibernationDelayMinutes?: IRangeNumber;
  ignitionOff?: IRangeDate;
  ignitionOn?: IRangeDate;
  ignitionDetectMethod?: string;
  initialDockDate?: IRangeDate;
  installationDate?: IRangeDate;
  installStreamsExpected?: string;
  installStreamsFound?: string;
  j1939VirtualIgnitionEnabled?: string;
  lastCommunicationDate?: IRangeDate;
  lastAccelerometerDateTime?: IRangeDate;
  lastTripDate?: IRangeDate;
  motionBasedIgnitionEnabled?: string;
  motionIntervalStart?: IRangeDate;
  motionWithoutIgnitionSpan?: IRangeNumber;
  name?: string;
  pendingFirmwareUpdate?: boolean;
  platforms?: string[];
  presenceOff?: IRangeDate;
  presenceOn?: IRangeDate;
  remainingWakeOnModemTime?: IRangeNumber;
  scrollId?: string;
  sdCardFailure?: IRangeDate;
  sdCardSuccess?: IRangeDate;
  systemVoltage?: IRangeNumber;
  trackPoint?: IRangeDate;
  upTime?: IRangeNumber;
  useScroll?: boolean;
  vehicleWidth?: IRangeNumber;
  vnetStreamEnabled?: string;
  operationalState?: string;
  obdiiProvider?: string;
  obdiiLookupProvider?: string;
  enableObd2ReqsOnlyIfVehLikelyOn?: string;
}

export interface IDeviceShadowRecord extends IDeviceShadowBase {
  batteryVoltage: IDeviceShadowValue;
  calPickStatus: string;
  cameraHeight: number;
  cameraOffset: number;
  checkIn: string;
  continuousDataEnabled: boolean;
  deviceDetectedBusType: IDeviceShadowValue;
  ecmEngineHours: IDeviceShadowValue;
  ecmEngineSpeed: IDeviceShadowValue;
  ecmOdometer: IDeviceShadowValue;
  ecmSpeed: IDeviceShadowValue;
  ecmVin: IDeviceShadowValue;
  factoryInstallDate: string;
  firmwareVersion: IDeviceShadowValue;
  hibernationDelayMinutes: number;
  ignitionOff: string;
  ignitionOn: string;
  ignitionDetectMethod: string;
  initialDockDate: string;
  installationDate: string;
  installStreamsExpected: string;
  installStreamsFound: string;
  j1939VirtualIgnitionEnabled: boolean;
  lastAccelerometerDateTime: string;
  lastCommunicationDate: string;
  hubLastCommunicationDate?: string;
  lastTripDate: string;
  motionBasedIgnitionEnabled: boolean;
  motionIntervalStart: string;
  motionWithoutIgnitionSpan: number;
  presenceOff: string;
  presenceOn: string;
  remainingWakeOnModemTime: IDeviceShadowValue;
  sdCardFailure: string;
  sdCardSuccess: string;
  systemVoltage: IDeviceShadowValue;
  trackPoint: IDeviceShadowValue;
  upTime: IDeviceShadowValue;
  vehicleWidth: number;
  vnetStreamEnabled: boolean;
  shippedCable: string;
  obdiiAccessories: string;
  obdiiProvider?: string;
  obdiiLookupProvider?: string;
  enableObd2ReqsOnlyIfVehLikelyOn?: boolean;
  hdVehicleAccessory: string;
  salesforceVehicleId: string;
  installBatteryVoltage: IDeviceShadowValue;
  installSdCardSuccess: string;
  installTrackPoint: IDeviceShadowValue;
  operationalState?: string;
  geotaConnectionState?: string;
  geotabLastCommunicated?: string;
  geotabSerialNumber?: string;
  lastPairedMessage?: string;
  deviceType?: string;
  connectionState?: boolean;
  ioxFirmwareVersion?: string;
  lastECMSignal?: string;
  latLong?: IDeviceShadowValue;
  heading?: string;
  troubleCodes?: DTC[];
  views?: View[];
}

export interface View {
  name: string;
  label: string;
  lastRecordedDate?: string;
  viewDisassociationAcknowledgedDate?: string;
}

export class DTC{
  code : string;
  timestamp : IRangeDate;
  failureMode : string;
  faultData : string;
  troubleCodes?: DiagnosticTroubleCode[];
}

export interface DiagnosticTroubleCode{
  code : string;
  timestamp : IRangeDate;
  failureMode : string;
  faultData : string;
}

export interface IDeviceShadowValue {
  name: string;
  timestamp: string;
  value: string;
 }
export interface IRangeDate {
  start: Date;
  end: Date;
}

export interface IRangeNumber {
  start: number;
  end: number;
}

export class ShadowQueryDateProperty {
  static readonly checkIn = 'checkIn';
  static readonly ecmEngineHours = 'ecmEngineHours';
  static readonly ecmEngineSpeed = 'ecmEngineSpeed';
  static readonly ecmOdometer = 'ecmOdometer';
  static readonly ecmSpeed = 'ecmSpeed';
  static readonly ecmVin = 'ecmVin';
  static readonly factoryInstallDate = 'factoryInstallDate';
  static readonly ignitionOff = 'ignitionOff';
  static readonly ignitionOn = 'ignitionOn';
  static readonly initialDockDate = 'initialDockDate';
  static readonly installationDate = 'installationDate';
  static readonly lastAccelerometerDateTime = 'lastAccelerometerDateTime';
  static readonly lastTripDate = 'lastTripDate';
  static readonly motionIntervalStart = 'motionIntervalStart';
  static readonly presenceOff = 'presenceOff';
  static readonly presenceOn = 'presenceOn';
  static readonly sdCardFailure = 'sdCardFailure';
  static readonly sdCardSuccess = 'sdCardSuccess';
  static readonly trackPoint = 'trackPoint';
  static readonly lastCommunicationDate = 'lastCommunicationDate';
  static readonly lastPairedMessage = 'lastPairedMessage';
  static readonly geotabLastCommunicated = 'geotabLastCommunicated';
}

export class ShadowQueryValueProperty {
  static readonly cableType = 'cableType';
  static readonly calPickStatus = 'calPickStatus';
  static readonly can1Type = 'can1Type';
  static readonly companyId = 'companyId';
  static readonly companyName = 'companyName';
  static readonly continuousDataEnabled = 'continuousDataEnabled';
  static readonly deviceDetectedBusType = 'deviceDetectedBusType';
  static readonly diliRecordNumber = 'diliRecordNumber';
  static readonly firmwareVersion = 'firmwareVersion';
  static readonly firmwareVersionAuthoritativeEntity =
    'firmwareVersionAuthoritativeEntity';
  static readonly followerSerial = 'followerSerial';
  static readonly followerState = 'followerState';
  static readonly groupName = 'groupName';
  static readonly geotabSerialNumber = 'geotabSerialNumber';
  static readonly geotabConnectionState = 'geotabConnectionState';
  static readonly deviceType = 'deviceType';
  static readonly hardwareVersion = 'hardwareVersion';
  static readonly ignitionDetectMethod = 'ignitionDetectMethod';
  static readonly installStreamsExpected = 'installStreamsExpected';
  static readonly installStreamsFound = 'installStreamsFound';
  static readonly installerType = 'installerType';
  static readonly intendedFirmwareVersion = 'intendedFirmwareVersion';
  static readonly j1939VirtualIgnitionEnabled = 'j1939VirtualIgnitionEnabled';
  static readonly motionBasedIgnitionEnabled = 'motionBasedIgnitionEnabled';
  static readonly pendingFirmwareUpdate = 'pendingFirmwareUpdate';
  static readonly platform = 'platform';
  static readonly serialNumber = 'serialNumber';
  static readonly statusId = 'statusId';
  static readonly vehicleCalibration = 'vehicleCalibration';
  static readonly vehicleClass = 'vehicleClass';
  static readonly vehicleId = 'vehicleId';
  static readonly vehicleMake = 'vehicleMake';
  static readonly vehicleModel = 'vehicleModel';
  static readonly vehicleName = 'vehicleName';
  static readonly vehicleYear = 'vehicleYear';
  static readonly vin = 'vin';
  static readonly vnetStreamEnabled = 'vnetStreamEnabled';
  static readonly operationalState = 'operationalState';
  static readonly obdiiProvider = 'obdiiProvider';
  static readonly obdiiLookupProvider = 'obdiiLookupProvider';
  static readonly enableObd2ReqsOnlyIfVehLikelyOn = 'enableObd2ReqsOnlyIfVehLikelyOn';
  static readonly heading = 'heading';
}

export class ShadowQueryValueRangeProperty {
  static readonly batteryVoltage = 'batteryVoltage';
  static readonly cameraHeight = 'cameraHeight';
  static readonly cameraOffset = 'cameraOffset';
  static readonly hibernationDelayMinutes = 'hibernationDelayMinutes';
  static readonly motionWithoutIgnitionSpan = 'motionWithoutIgnitionSpan';
  static readonly remainingWakeOnModemTime = 'remainingWakeOnModemTime';
  static readonly systemVoltage = 'systemVoltage';
  static readonly upTime = 'upTime';
  static readonly vehicleWidth = 'vehicleWidth';
  static readonly shippedCable = 'shippedCable';
  static readonly obdiiAccessories = 'obdiiAccessories';
  static readonly hdVehicleAccessory = 'hdVehicleAccessory';
  static readonly salesforceVehicleId = 'salesforceVehicleId';
}

export class ShadowQueryFilterOnValueOnlyProperty {
  static readonly ecmVinValue = 'ecmVinValue';
}

export interface CsvResponse {
  requestId: string;
}

export interface SignedUrlResponse {
  url: string;
  status: string;
}
